import { FC, useEffect } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightFromBracket, faChevronLeft, faChevronRight, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { usePersistentState } from "../../util/usePersistentState";
import { ModalProvider } from "./ModalProvider";
import { RefreshController } from "../RefreshController";
import { PermissionsResolver, usePermissions } from "@hostingbv/lib/src/auth/permissions";
import { Permission } from "../../api/common/models/PermissionMap";

export type NavigationDefinition = ({ link: string, icon: IconDefinition, text: string, beta?: true, permissionConstraints?: { resource: string, requiredPermission: Permission } } | { header: string })[]
export const AuthenticatedLayout: FC<{ children: React.ReactNode, client: PermissionsResolver, topChildren?: React.ReactNode, navigation: NavigationDefinition, title: string, subtitle: string, image: string | null }> = ({ children, client, topChildren, navigation, title, subtitle, image }) => {
  const { keycloak } = useKeycloak()
  const [isCollapsed, setIsCollapsed] = usePersistentState<boolean>('collapse_sidebar', false)
  const linkStyle = (props: { isActive: boolean, isPending: boolean }) => `h-10 flex items-center my-1 mx-2 rounded-lg ${props.isActive ? 'bg-brand-50 text-brand-700 text-black' : 'border-transparent text-slate-500  hover:bg-slate-50'} px-3 font-medium`
  const { can } = usePermissions(client)

  useEffect(() => {
    if (window?.document !== undefined) {
      window.document.querySelector('link[rel="icon"]')?.setAttribute('href', image ?? '/favicon.png');
    }
  }, [image])

  return <div className={"flex flex-col min-h-screen bg-slate-50"}>
    <RefreshController>
      <ModalProvider>
        <nav className={"bg-brand-600 text-white h-20 flex items-stretch justify-start"}>
          {image !== null ? <>
            <div className={"flex items-center justify-center ml-2 mr-4"}>
              <img src={image} className={"w-16 h-16 rounded-lg shadow-lg"} alt={""} />
            </div>
          </> : <div className={"w-6"} />}
          <div className={"flex-1 flex flex-col justify-center items-start pr-6"}>
            <h1 className={"font-bold text-xl leading-none"}>{title}.</h1>
            <span className={"font-light opacity-75 text-sm"}>{subtitle}</span>
          </div>
          <button className={"flex items-center hover:bg-[#0002] px-6"} onClick={() => keycloak.logout()}>
            <div className={"flex flex-col items-end justify-center mr-4"}>
              <small className={"leading-tight opacity-90"}>Ingelogd als {keycloak.idTokenParsed?.email ?? '-'}</small>
              <div className={"leading-tight"}>Uitloggen</div>
            </div>
            <FontAwesomeIcon icon={faArrowRightFromBracket} />
          </button>
        </nav>
        <div className={"flex-1 flex items-stretch"}>
          <aside className={`bg-white ${isCollapsed ? 'w-16' : 'w-48'} transition-all border-r border-slate-200 flex flex-col justify-between pt-8 overflow-hidden`}>
            {/* Navigation */}
            <div className={'flex flex-col'}>
              {navigation.filter(item => 'header' in item || !item.permissionConstraints || can(item.permissionConstraints.requiredPermission, item.permissionConstraints.resource)).map((item, i) => {
                if ('header' in item) {
                  if (isCollapsed) {
                    return <></>
                  }
                  return <div key={i} className={`mx-5 mt-5 mb-2 text-sm font-medium text-brand-800`}>{item.header}</div>
                }
                return <NavLink to={item.link} className={linkStyle} key={i}>
                  <div className={`text-center ${isCollapsed ? '' : 'mr-3'}`}>
                    <FontAwesomeIcon className={"w-6"} icon={item.icon} />
                  </div>
                  {!isCollapsed && <>
                    <span className={"flex-1"}>{item.text}</span>
                    {item.beta && <span
                      className={"text-[11px] px-[5px] py-px font-medium rounded-sm uppercase bg-brand-100 text-brand-900"}>Beta</span>}
                  </>}
                </NavLink>;
              })}
            </div>
            <button className={"hover:bg-slate-50 h-12"} onClick={() => setIsCollapsed(x => !x)}>
              <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronLeft} />
            </button>
          </aside>
          <main className={"flex-1 overflow-y-scroll"} style={{ height: 'calc(100vh - 5rem)' }}>
            {topChildren}
            <div className={"mx-6 my-8"}>
              {children}
            </div>
          </main>
        </div>
      </ ModalProvider>
    </RefreshController>
  </div>
}
